import React from 'react';
import { useCurrency } from '@src/hooks/useCurrency';
import useCurrentTextStyle from '../../../../hooks/useCurrentTextStyle';
import { useResponsiveBreakpoints } from '../../../../hooks/useMediaQuery';

// Extend window interface for TypeScript
declare global {
  interface Window {
    minicartDiscountThreshold?: number;
  }
}

// BNPL Component for reuse in different contexts
export const BNPLSection = ({ settings }) => {
  const { getCurrentTextStyle } = useCurrentTextStyle();

  // Buy Now Pay Later settings
  const showBnplImage = settings?.show_bnpl_image || false;
  const bnplImage = settings?.bnpl_image || '';
  const bnplImageUrl = settings?.bnpl_image_url || '';
  const bnplImageAlt = settings?.bnpl_image_alt || 'Buy Now Pay Later';
  const bnplImageStyle = settings?.bnpl_image_style || 'max-w-full h-auto';

  // Buy Now Pay Later text and icons settings with responsive support
  const bnplUrl = settings?.bnpl_url || '';
  const bnplText = settings?.bnpl_text || '';
  const bnplTextStyle = getCurrentTextStyle(
    settings?.bnpl_text_style,
    settings?.bnpl_text_style_enable_responsive,
    settings?.bnpl_text_style_mobile,
    settings?.bnpl_text_style_tablet,
    settings?.bnpl_text_style_ultrawide
  );
  const bnplTextColor = settings?.bnpl_text_color;
  const bnplIcon1 = settings?.bnpl_icon_1 || '';
  const bnplIcon2 = settings?.bnpl_icon_2 || '';
  const bnplIcon3 = settings?.bnpl_icon_3 || '';
  const bnplIcon4 = settings?.bnpl_icon_4 || '';
  const bnplIcon1Size = settings?.bnpl_icon_1_size || 24;
  const bnplIcon2Size = settings?.bnpl_icon_2_size || 24;
  const bnplIcon3Size = settings?.bnpl_icon_3_size || 24;
  const bnplIcon4Size = settings?.bnpl_icon_4_size || 24;

  return (
    <>
      {/* Buy Now Pay Later Image */}
      {showBnplImage && bnplImage && (
        <div className='bnpl__image mt-4 w-full flex justify-center'>
          {bnplImageUrl ? (
            <a
              href={bnplImageUrl}
              className='block'
            >
              <img
                src={bnplImage}
                alt={bnplImageAlt}
                className={bnplImageStyle}
              />
            </a>
          ) : (
            <img
              src={bnplImage}
              alt={bnplImageAlt}
              className={bnplImageStyle}
            />
          )}
        </div>
      )}

      {/* Buy Now Pay Later Text and Icons */}
      {(bnplText || bnplIcon1 || bnplIcon2 || bnplIcon3 || bnplIcon4) && (
        <div className='bnpl__text-icons mt-4 w-full flex items-center justify-center'>
          {bnplUrl ? (
            <a
              href={bnplUrl}
              className='flex items-center justify-center gap-2 hover:opacity-80 transition-opacity'
              target='_blank'
              rel='noopener noreferrer'
            >
              {/* BNPL Text */}
              {bnplText && (
                <div
                  className={bnplTextStyle}
                  style={{ color: bnplTextColor }}
                >
                  {bnplText}
                </div>
              )}

              {/* BNPL Icons */}
              {(bnplIcon1 || bnplIcon2 || bnplIcon3) && (
                <div className='flex items-center justify-center gap-3'>
                  {bnplIcon1 && (
                    <img
                      src={bnplIcon1}
                      alt='BNPL Payment Option 1'
                      style={{
                        width: `${bnplIcon1Size}px`,
                        height: `auto`,
                        objectFit: 'contain'
                      }}
                    />
                  )}
                  {bnplIcon2 && (
                    <img
                      src={bnplIcon2}
                      alt='BNPL Payment Option 2'
                      style={{
                        width: `${bnplIcon2Size}px`,
                        height: `auto`,
                        objectFit: 'contain'
                      }}
                    />
                  )}
                  {bnplIcon3 && (
                    <img
                      src={bnplIcon3}
                      alt='BNPL Payment Option 3'
                      style={{
                        width: `${bnplIcon3Size}px`,
                        height: `auto`,
                        objectFit: 'contain'
                      }}
                    />
                  )}
                  {bnplIcon4 && (
                    <img
                      src={bnplIcon4}
                      alt='BNPL Payment Option 4'
                      style={{
                        width: `${bnplIcon4Size}px`,
                        height: `auto`,
                        objectFit: 'contain'
                      }}
                    />
                  )}
                </div>
              )}
            </a>
          ) : (
            <div className='flex items-center justify-center gap-2'>
              {/* BNPL Text */}
              {bnplText && (
                <div
                  className={bnplTextStyle}
                  style={{ color: bnplTextColor }}
                >
                  {bnplText}
                </div>
              )}

              {/* BNPL Icons */}
              {(bnplIcon1 || bnplIcon2 || bnplIcon3 || bnplIcon4) && (
                <div className='flex items-center justify-center gap-3'>
                  {bnplIcon1 && (
                    <img
                      src={bnplIcon1}
                      alt='BNPL Payment Option 1'
                      style={{
                        width: `${bnplIcon1Size}px`,
                        height: `auto`,
                        objectFit: 'contain'
                      }}
                    />
                  )}
                  {bnplIcon2 && (
                    <img
                      src={bnplIcon2}
                      alt='BNPL Payment Option 2'
                      style={{
                        width: `${bnplIcon2Size}px`,
                        height: `auto`,
                        objectFit: 'contain'
                      }}
                    />
                  )}
                  {bnplIcon3 && (
                    <img
                      src={bnplIcon3}
                      alt='BNPL Payment Option 3'
                      style={{
                        width: `${bnplIcon3Size}px`,
                        height: `auto`,
                        objectFit: 'contain'
                      }}
                    />
                  )}
                  {bnplIcon4 && (
                    <img
                      src={bnplIcon4}
                      alt='BNPL Payment Option 4'
                      style={{
                        width: `${bnplIcon4Size}px`,
                        height: `auto`,
                        objectFit: 'contain'
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

interface MinicartTotalsProps {
  cart_subtotal: number;
  cart_items: any[];
  cart?: any; // Full cart object for accessing discounts, taxes, etc.
  block?: any;
  type?: 'minicart' | 'cart';
  settings: {
    // Content settings
    show_subtotal?: boolean;
    show_savings?: boolean;
    show_discount_codes?: boolean;
    show_gift_cards?: boolean;
    show_decimals?: boolean;
    subtotal_label_text?: string;
    subtotal_label_style?: string;
    subtotal_label_style_enable_responsive?: boolean;
    subtotal_label_style_mobile?: string;
    subtotal_label_style_tablet?: string;
    subtotal_label_style_ultrawide?: string;
    subtotal_label_text_color?: string;
    subtotal_value_style?: string;
    subtotal_value_style_enable_responsive?: boolean;
    subtotal_value_style_mobile?: string;
    subtotal_value_style_tablet?: string;
    subtotal_value_style_ultrawide?: string;
    subtotal_value_text_color?: string;
    savings_label_text?: string;
    savings_label_style?: string;
    savings_label_style_enable_responsive?: boolean;
    savings_label_style_mobile?: string;
    savings_label_style_tablet?: string;
    savings_label_style_ultrawide?: string;
    savings_label_text_color?: string;
    savings_value_text_style?: string;
    savings_value_style_enable_responsive?: boolean;
    savings_value_style_mobile?: string;
    savings_value_style_tablet?: string;
    savings_value_style_ultrawide?: string;
    savings_value_text_color?: string;
    discount_label_text?: string;
    discount_label_text_style?: string;
    discount_label_text_style_enable_responsive?: boolean;
    discount_label_text_style_mobile?: string;
    discount_label_text_style_tablet?: string;
    discount_label_text_style_ultrawide?: string;
    discount_label_text_color?: string;
    discount_value_text_style?: string;
    discount_value_text_style_enable_responsive?: boolean;
    discount_value_text_style_mobile?: string;
    discount_value_text_style_tablet?: string;
    discount_value_text_style_ultrawide?: string;
    discount_value_text_color?: string;
    total_label_text?: string;
    total_label_style?: string;
    total_label_style_enable_responsive?: boolean;
    total_label_style_mobile?: string;
    total_label_style_tablet?: string;
    total_label_style_ultrawide?: string;
    total_label_text_color?: string;
    total_value_style?: string;
    total_value_style_enable_responsive?: boolean;
    total_value_style_mobile?: string;
    total_value_style_tablet?: string;
    total_value_style_ultrawide?: string;
    total_value_text_color?: string;
    shipping_label_text?: string;
    shipping_label_text_style?: string;
    shipping_label_text_style_enable_responsive?: boolean;
    shipping_label_text_style_mobile?: string;
    shipping_label_text_style_tablet?: string;
    shipping_label_text_style_ultrawide?: string;
    shipping_label_text_color?: string;
    shipping_value_text?: string;
    shipping_value_text_style?: string;
    shipping_value_text_style_enable_responsive?: boolean;
    shipping_value_text_style_mobile?: string;
    shipping_value_text_style_tablet?: string;
    shipping_value_text_style_ultrawide?: string;
    shipping_value_text_color?: string;
    free_shipping_value_text?: string;

    // Appearance settings
    color_schema?: string;
    border_radius?: number;

    // Layout & Spacing settings
    direction?: string;
    padding_top?: number;
    padding_top_enable_responsive?: boolean;
    padding_top_mobile?: number;
    padding_top_tablet?: number;
    padding_top_ultrawide?: number;
    padding_bottom?: number;
    padding_bottom_enable_responsive?: boolean;
    padding_bottom_mobile?: number;
    padding_bottom_tablet?: number;
    padding_bottom_ultrawide?: number;
    padding_left_right?: number;
    padding_left_right_enable_responsive?: boolean;
    padding_left_right_mobile?: number;
    padding_left_right_tablet?: number;
    padding_left_right_ultrawide?: number;
    margin_top?: number;
    margin_bottom?: number;
    margin_left_right?: number;

    // Buy Now Pay Later settings
    show_bnpl_image?: boolean;
    bnpl_image?: string;
    bnpl_image_url?: string;
    bnpl_image_alt?: string;
    bnpl_image_style?: string;

    // Buy Now Pay Later text and icons settings
    bnpl_url?: string;
    bnpl_text?: string;
    bnpl_text_style?: string;
    bnpl_text_style_enable_responsive?: boolean;
    bnpl_text_style_mobile?: string;
    bnpl_text_style_tablet?: string;
    bnpl_text_style_ultrawide?: string;
    bnpl_text_color?: string;
    bnpl_icon_1?: string;
    bnpl_icon_2?: string;
    bnpl_icon_3?: string;
    bnpl_icon_4?: string;
    bnpl_icon_1_size?: number;
    bnpl_icon_2_size?: number;
    bnpl_icon_3_size?: number;
    bnpl_icon_4_size?: number;

    // Divider settings
    show_divider?: boolean;
    divider_color?: string;
    divider_thickness?: number;

    // Legacy settings (for backward compatibility)
    savings_value_style?: string;
    discount_label_style?: string;
    discount_value_style?: string;
  };
}

export const MinicartTotals = ({
  cart_subtotal,
  cart_items,
  cart,
  settings,
  block,
  type = 'minicart'
}: MinicartTotalsProps) => {
  if (!cart_items?.length) return null;

  const { convertPrice } = useCurrency();
  const { getCurrentTextStyle } = useCurrentTextStyle();
  const { isMobile, isTablet, isUltrawide } = useResponsiveBreakpoints();

  // Helper function to get responsive padding values
  const getResponsivePadding = (
    desktop: number | undefined,
    enableResponsive: boolean | undefined,
    mobile: number | undefined,
    tablet: number | undefined,
    ultrawide: number | undefined
  ) => {
    if (!enableResponsive) return desktop ?? 0;
    
    if (isMobile && mobile !== undefined) return mobile;
    if (isTablet && tablet !== undefined) return tablet;
    if (isUltrawide && ultrawide !== undefined) return ultrawide;
    return desktop ?? 0;
  };

  // Content settings
  const showSubtotal = settings?.show_subtotal !== false;
  const showSavings = settings?.show_savings !== false;
  const showDiscountCodes = settings?.show_discount_codes !== false;
  const showGiftCards = settings?.show_gift_cards !== false;
  const showDecimals = settings?.show_decimals !== false;

  // Text settings with responsive support and fallback to legacy
  const subtotalLabelText = settings?.subtotal_label_text || 'SUBTOTAL';
  const subtotalLabelTextStyle = getCurrentTextStyle(
    settings?.subtotal_label_style,
    settings?.subtotal_label_style_enable_responsive,
    settings?.subtotal_label_style_mobile,
    settings?.subtotal_label_style_tablet,
    settings?.subtotal_label_style_ultrawide
  );
  const subtotalLabelTextColor = settings?.subtotal_label_text_color;
  
  const subtotalValueTextStyle = getCurrentTextStyle(
    settings?.subtotal_value_style || subtotalLabelTextStyle,
    settings?.subtotal_value_style_enable_responsive,
    settings?.subtotal_value_style_mobile,
    settings?.subtotal_value_style_tablet,
    settings?.subtotal_value_style_ultrawide
  );
  const subtotalValueTextColor = settings?.subtotal_value_text_color;
  
  const savingsLabelText = settings?.savings_label_text || 'TOTAL SAVINGS';
  const savingsLabelTextStyle = getCurrentTextStyle(
    settings?.savings_label_style || subtotalLabelTextStyle,
    settings?.savings_label_style_enable_responsive,
    settings?.savings_label_style_mobile,
    settings?.savings_label_style_tablet,
    settings?.savings_label_style_ultrawide
  );
  const savingsLabelTextColor = settings?.savings_label_text_color;
  
  const savingsValueTextStyle = getCurrentTextStyle(
    settings?.savings_value_style || settings?.savings_value_text_style || subtotalLabelTextStyle,
    settings?.savings_value_style_enable_responsive,
    settings?.savings_value_style_mobile,
    settings?.savings_value_style_tablet,
    settings?.savings_value_style_ultrawide
  );
  const savingsValueTextColor = settings?.savings_value_text_color;
  
  const discountLabelText = settings?.discount_label_text || 'DISCOUNT';
  const discountLabelTextStyle = getCurrentTextStyle(
    settings?.discount_label_style || settings?.discount_label_text_style,
    settings?.discount_label_text_style_enable_responsive,
    settings?.discount_label_text_style_mobile,
    settings?.discount_label_text_style_tablet,
    settings?.discount_label_text_style_ultrawide
  );
  const discountLabelTextColor = settings?.discount_label_text_color;
  
  const discountValueTextStyle = getCurrentTextStyle(
    settings?.discount_value_style || settings?.discount_value_text_style,
    settings?.discount_value_text_style_enable_responsive,
    settings?.discount_value_text_style_mobile,
    settings?.discount_value_text_style_tablet,
    settings?.discount_value_text_style_ultrawide
  );
  const discountValueTextColor = settings?.discount_value_text_color;
  
  const totalLabelText = settings?.total_label_text || 'TOTAL';
  const totalLabelTextStyle = getCurrentTextStyle(
    settings?.total_label_style,
    settings?.total_label_style_enable_responsive,
    settings?.total_label_style_mobile,
    settings?.total_label_style_tablet,
    settings?.total_label_style_ultrawide
  );
  const totalLabelTextColor = settings?.total_label_text_color;
  
  const totalValueTextStyle = getCurrentTextStyle(
    settings?.total_value_style || totalLabelTextStyle,
    settings?.total_value_style_enable_responsive,
    settings?.total_value_style_mobile,
    settings?.total_value_style_tablet,
    settings?.total_value_style_ultrawide
  );
  const totalValueTextColor = settings?.total_value_text_color;
  
  const shippingLabelText = settings?.shipping_label_text || 'SHIPPING';
  const shippingLabelTextStyle = getCurrentTextStyle(
    settings?.shipping_label_text_style,
    settings?.shipping_label_text_style_enable_responsive,
    settings?.shipping_label_text_style_mobile,
    settings?.shipping_label_text_style_tablet,
    settings?.shipping_label_text_style_ultrawide
  );
  const shippingLabelTextColor = settings?.shipping_label_text_color;
  
  const shippingValueText = settings?.shipping_value_text || 'calculated at checkout';
  const shippingValueTextStyle = getCurrentTextStyle(
    settings?.shipping_value_text_style || shippingLabelTextStyle,
    settings?.shipping_value_text_style_enable_responsive,
    settings?.shipping_value_text_style_mobile,
    settings?.shipping_value_text_style_tablet,
    settings?.shipping_value_text_style_ultrawide
  );
  const shippingValueTextColor = settings?.shipping_value_text_color;

  const freeShippingValueText = settings?.free_shipping_value_text || 'Free';

  // Get discount threshold from global window object set by MinicartDiscounts
  const discountThreshold = (typeof window !== 'undefined' ? window.minicartDiscountThreshold : 0) || 0;
  const isFreeShippingThresholdMet = discountThreshold > 0 && cart_subtotal >= discountThreshold;

  // Layout & Spacing
  const direction = settings?.direction || 'column';
  const paddingTop = getResponsivePadding(
    settings?.padding_top,
    settings?.padding_top_enable_responsive,
    settings?.padding_top_mobile,
    settings?.padding_top_tablet,
    settings?.padding_top_ultrawide
  );
  const paddingBottom = getResponsivePadding(
    settings?.padding_bottom,
    settings?.padding_bottom_enable_responsive,
    settings?.padding_bottom_mobile,
    settings?.padding_bottom_tablet,
    settings?.padding_bottom_ultrawide
  );
  const paddingLeftRight = getResponsivePadding(
    settings?.padding_left_right,
    settings?.padding_left_right_enable_responsive,
    settings?.padding_left_right_mobile,
    settings?.padding_left_right_tablet,
    settings?.padding_left_right_ultrawide
  );
  const marginTop = settings?.margin_top ?? 0;
  const marginBottom = settings?.margin_bottom ?? 20;
  const marginLeftRight = settings?.margin_left_right ?? 0;

  // Appearance
  const borderRadius = settings?.border_radius || 0;

  // Divider settings
  const showDivider = settings?.show_divider || false;
  const dividerColor = settings?.divider_color || '#e5e7eb';
  const dividerThickness = settings?.divider_thickness || 1;

  // Calculate total savings for items with a selling plan or discounts
  const totalSavings = cart_items.reduce((acc, item) => {
    if (item.selling_plan_allocation) {
      // Subscription savings
      const originalPrice = item.selling_plan_allocation.compare_at_price;
      const discountedPrice = item.selling_plan_allocation.price;
      const itemSavings = ((originalPrice - discountedPrice) / 100) * item.quantity;
      return acc + itemSavings;
    } else if (item.line_level_discount_allocations?.length) {
      // Discount savings
      let itemDiscount = 0;
      item.line_level_discount_allocations.forEach((discount) => {
        itemDiscount += discount.amount;
      });
      const itemSavings = itemDiscount / 100;
      return acc + itemSavings;
    } else if (item.product?.compare_at_price) {
      // Compare at price savings
      const originalPrice = item.product.compare_at_price;
      const discountedPrice = item.price;
      const itemSavings = ((originalPrice - discountedPrice) / 100) * item.quantity;
      return acc + itemSavings;
    }
    return acc;
  }, 0);

  const totalBeforeSavings = totalSavings + cart_subtotal;
  const savings = cart?.total_discount / 100 || 0;
  const hasSavings = savings > 0;

  // Calculate cart-level discounts (from discount codes)
  const cartDiscountAmount = cart?.total_discount ? cart.total_discount / 100 : 0;
  const hasCartDiscounts = cartDiscountAmount > 0;

  // Get applied discount codes
  const appliedDiscountCodes = cart?.discount_codes || [];

  // Get applied gift cards
  const appliedGiftCards = cart?.applied_gift_cards || [];
  const giftCardAmount = appliedGiftCards.reduce((total: number, giftCard: any) => {
    const amount = giftCard.amountUsed || giftCard.amount_used || 0;
    return total + (typeof amount === 'number' ? amount / 100 : parseFloat(amount.amount || '0'));
  }, 0);
  const hasGiftCards = giftCardAmount > 0;

  // Calculate the final total after all discounts
  const finalTotal = cart?.total_price ? cart.total_price / 100 : cart_subtotal;

  const staticCurrencySymbol = (window as any).store_currency_symbol || '$';
  const { currentCurrencySymbol } = useCurrency();
  const currencySymbolDisplay = staticCurrencySymbol || currentCurrencySymbol;

  // Helper function to format currency based on decimal setting
  const formatCurrency = (amount: number, forceDecimals = false) => {
    const decimals = showDecimals || forceDecimals ? 2 : 0;
    return `${currencySymbolDisplay}${amount.toFixed(decimals)}`;
  };

  // Container styles
  const containerStyle = {
    paddingTop: `${paddingTop}px`,
    paddingBottom: `${paddingBottom}px`,
    paddingLeft: `${paddingLeftRight}px`,
    paddingRight: `${paddingLeftRight}px`,
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    marginLeft: `${marginLeftRight}px`,
    marginRight: `${marginLeftRight}px`,
    borderRadius: `${borderRadius}px`,
    display: 'flex',
    flexDirection: direction === 'row' ? 'row' : 'column',
    width: '100%'
  } as React.CSSProperties;

  // Get the correct color schema from block data
  const colorSchema = block?.color_schema || settings?.color_schema;

  return (
    <div
      className={`minicart__totals ${colorSchema ? `color-${colorSchema}` : ''} flex-col`}
      style={containerStyle}
    >
      {/* Subtotal Row */}
      {showSubtotal && (
        <div className='minicart__totals-row w-full flex justify-between items-center '>
          <div
            className={subtotalLabelTextStyle}
            style={{ color: subtotalLabelTextColor }}
          >
            {subtotalLabelText}
          </div>
          <div
            className={subtotalValueTextStyle}
            style={{ color: subtotalValueTextColor }}
          >
            {formatCurrency(cart_subtotal)}
          </div>
        </div>
      )}

      {/* Savings Row */}
      {showSavings && hasSavings && (
        <div className='minicart__totals-row  w-full flex justify-between items-center '>
          <div
            className={savingsLabelTextStyle}
            style={{ color: savingsLabelTextColor }}
          >
            {savingsLabelText}
          </div>
          <div
            className={savingsValueTextStyle}
            style={{ color: savingsValueTextColor }}
          >
            -{formatCurrency(savings, true)}
          </div>
        </div>
      )}

      <div className='minicart__totals-row w-full flex justify-between items-center '>
        <div
          className={shippingLabelTextStyle}
          style={{ color: shippingLabelTextColor }}
        >
          {shippingLabelText}
        </div>
        <div
          className={shippingValueTextStyle}
          style={{ color: shippingValueTextColor }}
        >
          {isFreeShippingThresholdMet ? freeShippingValueText : shippingValueText}
        </div>
      </div>

      {/* Applied Discount Codes */}
      {showDiscountCodes && appliedDiscountCodes.length > 0 && (
        <div className='minicart__discount-codes py-1'>
          {appliedDiscountCodes.map((discountCode: any, index: number) => {
            const code = typeof discountCode === 'string' ? discountCode : discountCode.code;
            return (
              <div
                key={index}
                className='minicart__totals-row w-full flex justify-between items-center py-0.5'
              >
                <div
                  className={discountLabelTextStyle}
                  style={{ color: discountLabelTextColor }}
                >
                  {`${discountLabelText}: ${code}`}
                </div>
                <div
                  className={discountValueTextStyle}
                  style={{ color: discountValueTextColor }}
                >
                  {hasCartDiscounts ? `-${formatCurrency(cartDiscountAmount, true)}` : 'Applied'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Applied Gift Cards */}
      {showGiftCards && appliedGiftCards.length > 0 && (
        <div className='minicart__gift-cards py-1'>
          {appliedGiftCards.map((giftCard: any, index: number) => {
            const amount = giftCard.amountUsed || giftCard.amount_used || 0;
            const displayAmount = typeof amount === 'number' ? amount / 100 : parseFloat(amount.amount || '0');
            const lastChars = giftCard.lastCharacters || (giftCard.code ? giftCard.code.slice(-4) : '****');

            return (
              <div
                key={index}
                className='minicart__totals-row w-full flex justify-between items-center py-0.5'
              >
                <div
                  className={discountLabelTextStyle}
                  style={{ color: discountLabelTextColor }}
                >
                  {`Gift Card: ****${lastChars}`}
                </div>
                <div
                  className={discountValueTextStyle}
                  style={{ color: discountValueTextColor }}
                >
                  {`-${formatCurrency(displayAmount, true)}`}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Total Row */}
      <div
        className={`minicart__totals-row flex  w-full justify-between items-center`}
      >
        <div
          className={totalLabelTextStyle}
          style={{ color: totalLabelTextColor }}
        >
          {totalLabelText}
        </div>
        <div
          className={totalValueTextStyle}
          style={{ color: totalValueTextColor }}
        >
          {formatCurrency(finalTotal)}
        </div>
      </div>

      {/* Buy Now Pay Later - Only show in minicart, not in cart */}
      {type !== 'cart' && <BNPLSection settings={settings} />}

      {/* Divider */}
      {showDivider && (
        <div
          style={{
            width: '100%',
            height: dividerThickness,
            backgroundColor: dividerColor
          }}
        />
      )}
    </div>
  );
};
